/**
 * The feng shui first run's starter room and its saved example layout.
 *
 * This is the "coffin position" bedroom from the AI ad (Concept E in
 * [marketing/reels-scripts.md](../../marketing/reels-scripts.md)). The ad's
 * Remotion project reads its room, door, window and before/after poses
 * from here, so the ad and the landing room can't drift apart.
 *
 * The example suggestion is authored to mirror the ad, not generated per
 * visit: it plays instantly, costs no credit, needs no sign-in, and is
 * always labelled as an example in the UI. It only moves pieces
 * (Rearrange mode) and passes the same fit / overlap / door-clearance
 * rules a real result must (see `fengShuiExample.test.ts`).
 *
 * Coordinates are room inches, origin at the inside top-left corner, +y
 * down. At rotation 0 a piece's front (+y) faces down.
 */

import { presetForName, presetToFurniture } from "./catalog";
import type { FengShuiSuggestion } from "./fengShui";
import { presetShape } from "./geom";
import type { Design, Furniture, Opening } from "./types";

export const FENG_SHUI_STARTER_ROOM_ID = "starter_feng_shui";

/**
 * 14'6" × 11'0" — wide enough for a commanding desk (chair behind it, back
 * to the wall) plus a 36" walkway past the foot of the bed.
 */
export const FENG_SHUI_ROOM = { width: 174, depth: 132 } as const;

/**
 * Standard door on the bottom wall. Wall 2 of a rectangle preset runs
 * right → left, so `alongWall: 76` centres it at x = 98 — dead in line
 * with the bed's "before" position.
 */
export const FENG_SHUI_DOOR: Opening = {
  id: "fs_door",
  name: "Door",
  kind: "door",
  wallIndex: 2,
  alongWall: 76,
  width: 36,
  height: 80,
  fromFloor: 40,
};

/** Medium window on the top wall (wall 0 runs left → right). */
export const FENG_SHUI_WINDOW: Opening = {
  id: "fs_window",
  name: "Window",
  kind: "window",
  wallIndex: 0,
  alongWall: 61,
  width: 48,
  height: 48,
  fromFloor: 48,
};

/** Door and window as x-spans on their walls, for plan-view drawing. */
export const FENG_SHUI_DOOR_SPAN = {
  x0:
    FENG_SHUI_ROOM.width - FENG_SHUI_DOOR.alongWall - FENG_SHUI_DOOR.width / 2,
  x1:
    FENG_SHUI_ROOM.width - FENG_SHUI_DOOR.alongWall + FENG_SHUI_DOOR.width / 2,
};
export const FENG_SHUI_WINDOW_SPAN = {
  x0: FENG_SHUI_WINDOW.alongWall - FENG_SHUI_WINDOW.width / 2,
  x1: FENG_SHUI_WINDOW.alongWall + FENG_SHUI_WINDOW.width / 2,
};

export interface Pose {
  x: number;
  y: number;
  /** Degrees. Signed so animations can take the intended turn. */
  rot: number;
}

export type ExamplePieceKey =
  | "bed"
  | "nightstandA"
  | "nightstandB"
  | "desk"
  | "chair"
  | "dresser"
  | "plant";

export interface ExamplePiece {
  key: ExamplePieceKey;
  /** Catalog preset name. */
  preset: string;
  /** Short on-canvas label. */
  label: string;
  before: Pose;
  after: Pose;
  /** The per-piece "why", shown in the sheet and the inspector. */
  reason: string;
}

// Before: bed in line with the door (the coffin position), desk facing a
// wall with its back to the door, dresser stranded in the walkway.
// After: headboard on a solid wall diagonal from the door, nightstands
// flanking it, desk on the far wall with the chair behind it (sitter faces
// the room and sees the door), dresser under the window with its drawers
// toward the room, plant in the far-left (Wealth) corner. Walkways: 36" past
// the foot of the bed, ~29" along both sides, open floor to the desk chair.
export const EXAMPLE_PIECES: readonly ExamplePiece[] = [
  {
    key: "bed",
    preset: "Full bed",
    label: "Full bed",
    before: { x: 98, y: 39, rot: 0 },
    after: { x: 39, y: 76, rot: -90 },
    reason:
      "Headboard on a solid wall with a clear view of the door. That's the commanding position.",
  },
  {
    key: "nightstandA",
    preset: "Nightstand",
    label: "Nightstand",
    before: { x: 59, y: 10.5, rot: 0 },
    after: { x: 10.5, y: 37, rot: -90 },
    reason:
      "One nightstand on each side of the headboard keeps the bed balanced.",
  },
  {
    key: "nightstandB",
    preset: "Nightstand",
    label: "Nightstand",
    before: { x: 19, y: 117, rot: 14 },
    after: { x: 10.5, y: 115, rot: -90 },
    reason: "Pairs with the other nightstand so the bed is flanked evenly.",
  },
  {
    key: "desk",
    preset: "Desk",
    label: "Desk",
    before: { x: 15.5, y: 58, rot: -90 },
    after: { x: 126.5, y: 45, rot: -90 },
    reason:
      "Faces into the room with a solid wall behind the chair, so you can see the door and who comes in.",
  },
  {
    key: "chair",
    preset: "Dining chair",
    label: "Chair",
    before: { x: 41.5, y: 58, rot: 90 },
    after: { x: 150.5, y: 45, rot: 90 },
    reason:
      "Behind the desk with room to push back, and an open path to it from the door.",
  },
  {
    key: "dresser",
    preset: "Dresser",
    label: "Dresser",
    before: { x: 60, y: 92, rot: 8 },
    after: { x: 58, y: 10.5, rot: 0 },
    reason:
      "Out of the walkway and under the window, drawers facing the room, so qi can flow in from the door.",
  },
  {
    key: "plant",
    preset: "Plant (large)",
    label: "Plant",
    before: { x: 140, y: 110, rot: 0 },
    after: { x: 13.5, y: 13.5, rot: 0 },
    reason:
      "Wood element in the far-left corner from the door: the Wealth area of the bagua.",
  },
];

export const EXAMPLE_OVERALL =
  "The bed was in the coffin position, with its feet pointing straight out the door. " +
  "Moving the headboard to the solid wall puts it in the commanding position, the desk now " +
  "faces the room with the door in view, and clear walkways let qi flow in from the door.";

/** The example's "Feng shui score" before and after, as shown in the ad. */
export const EXAMPLE_SCORE = { before: 38, after: 94 } as const;

/**
 * The on-canvas callouts, word for word from the ad. The landing draws
 * them on the real canvas (red issues on the "before" room, green fixes as
 * each piece lands), and the ad's Remotion project imports them, so the
 * two can't drift apart.
 */
export const EXAMPLE_ISSUES = {
  coffin: "Coffin position",
  desk: "Back to the door",
  dresser: "qi blocked",
} as const;
export type ExampleIssueKey = keyof typeof EXAMPLE_ISSUES;

export const EXAMPLE_FIXES = {
  bed: "Commanding position",
  nightstands: "Balanced",
  desk: "Sees the door",
  chi: "qi flows freely",
  plant: "Wealth corner",
} as const;
export type ExampleFixKey = keyof typeof EXAMPLE_FIXES;

/** Where each callout sits (room inches). */
export const EXAMPLE_TAG_AT: Record<
  ExampleIssueKey | ExampleFixKey | "deskFix",
  { x: number; y: number }
> = {
  coffin: { x: 98, y: 46 },
  desk: { x: 30, y: 36 },
  dresser: { x: 60, y: 92 },
  bed: { x: 39, y: 76 },
  nightstands: { x: 42, y: 118 },
  deskFix: { x: 134, y: 88 },
  chi: { x: 146, y: 114 },
  plant: { x: 24, y: 30 },
};

const DOOR_CENTER_X = (FENG_SHUI_DOOR_SPAN.x0 + FENG_SHUI_DOOR_SPAN.x1) / 2;

/** Door → foot of the bed: the coffin line drawn on the "before" room. */
export const EXAMPLE_COFFIN_LINE = {
  from: { x: DOOR_CENTER_X, y: 127 },
  to: { x: DOOR_CENTER_X, y: 81 },
} as const;

/** Where qi enters and meanders once the room is fixed: in the door, up
 *  the walkway past the foot of the bed, out toward the window. */
export const EXAMPLE_CHI_POINTS: readonly { x: number; y: number }[] = [
  { x: DOOR_CENTER_X, y: 128 },
  { x: 106, y: 106 },
  { x: 91, y: 88 },
  { x: 96, y: 62 },
  { x: 101, y: 36 },
];

/**
 * The fix sequence in milliseconds from the moment the rearrangement
 * starts. The ad's timeline converts these to frames, and the landing's
 * in-canvas player drives its moves, callouts and score from them, so a
 * visitor sees the same beats the Reel showed.
 */
export const EXAMPLE_SEQUENCE = {
  /** "Consulting the bagua…" before the first piece moves. */
  thinkMs: 900,
  /** How long one piece takes to settle. */
  moveMs: 800,
  moveAtMs: {
    desk: 200,
    chair: 233,
    dresser: 367,
    bed: 500,
    nightstandA: 667,
    nightstandB: 733,
    plant: 867,
  } satisfies Record<ExamplePieceKey, number>,
  fixAtMs: {
    bed: 1467,
    nightstands: 1933,
    desk: 2400,
    chi: 2933,
    plant: 3400,
  } satisfies Record<ExampleFixKey, number>,
  /** The score has finished climbing 38 → 94. */
  scoreDoneMs: 3800,
  /** The bed's "Why it's here" opens. */
  whyAtMs: 4267,
} as const;

export const examplePieceId = (key: ExamplePieceKey) => `fs_${key}`;

const normalizeDeg = (deg: number) => ((deg % 360) + 360) % 360;

function placePiece(piece: ExamplePiece): Furniture {
  const preset = presetForName(piece.preset);
  if (!preset) throw new Error(`Unknown preset: ${piece.preset}`);
  return {
    ...presetToFurniture(preset, piece.before.x, piece.before.y),
    id: examplePieceId(piece.key),
    rotation: normalizeDeg(piece.before.rot),
  };
}

/** The furnished "before" room the feng shui first run lands on. */
export function buildFengShuiStarterDesign(): Design {
  return {
    room: {
      id: FENG_SHUI_STARTER_ROOM_ID,
      name: "My bedroom",
      points: presetShape(
        "rectangle",
        FENG_SHUI_ROOM.width,
        FENG_SHUI_ROOM.depth
      ),
      ceilingHeight: 96,
      wallThickness: 5,
      shapeKind: "rectangle",
    },
    furniture: EXAMPLE_PIECES.map(placePiece),
    mounts: [],
    openings: [{ ...FENG_SHUI_DOOR }, { ...FENG_SHUI_WINDOW }],
    person: { heightInches: 71, weightLbs: 170, position: null },
    unit: "imperial",
    consultations: [],
    rankings: [],
  };
}

/**
 * The saved example result, keyed to the pieces still in `design`. Pieces
 * the visitor deleted are skipped; nothing is added or removed.
 */
export function buildExampleSuggestion(design: Design): FengShuiSuggestion {
  const ids = new Set(design.furniture.map((f) => f.id));
  return {
    updates: EXAMPLE_PIECES.filter((p) => ids.has(examplePieceId(p.key))).map(
      (p) => ({
        id: examplePieceId(p.key),
        x: p.after.x,
        y: p.after.y,
        rotation: normalizeDeg(p.after.rot),
        reason: p.reason,
      })
    ),
    additions: [],
    removals: [],
    mountUpdates: [],
    mountAdditions: [],
    mountRemovals: [],
    overall: EXAMPLE_OVERALL,
  };
}

/** True while the design is still the untouched feng shui starter room. */
export function isFengShuiStarterRoom(design: Pick<Design, "room">): boolean {
  return design.room.id === FENG_SHUI_STARTER_ROOM_ID;
}
